"use client";

import React from "react";
import { Sprout, Clock, Flame, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Wild Foraging",
  description: "Hand-picked ingredients from the deep Danish wilderness.",
  icon: <Sprout className="w-6 h-6 text-primary" />,
  progress: 88
}, {
  title: "Strictly Seasonal",
  description: "A menu that shifts with the rhythm of the northern sun.",
  icon: <Clock className="w-6 h-6 text-primary" />,
  progress: 95
}, {
  title: "Zero Waste",
  description: "Honoring the whole ingredient, from root to leaf.",
  icon: <Flame className="w-6 h-6 text-primary" />,
  progress: 100
}];
export default function TheFreshStandard() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="67295" id="from-soil-to-plate" ref={sectionRef} className="dark bg-background text-foreground w-full overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[700px]">
        {}
        <div className={`relative h-[400px] lg:h-auto w-full overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/danish_forest_floor.png)] bg-cover bg-center transition-transform duration-1000 hover:scale-105" />
          <div className="absolute inset-0 bg-black/40" /> {}
        </div>

        {}
        <div className="flex flex-col justify-center px-6 py-16 lg:px-20 lg:py-24 bg-card/30 backdrop-blur-sm relative">
          {}
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <div className="w-24 h-24 border-t border-r border-primary" />
          </div>
          <div className="absolute bottom-0 left-0 p-4 opacity-10">
            <div className="w-24 h-24 border-b border-l border-primary" />
          </div>

          <div className={`space-y-10 transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-4">
              <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-primary mb-2 block">Our Philosophy</span>
              <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tighter leading-[0.9]">
                From Soil <br />
                <span className="font-mono italic text-primary text-4xl md:text-6xl">to Plate.</span>
              </h2>
              <p className="text-muted-foreground text-lg md:text-xl max-w-md font-medium leading-[1.8]">
                Our cuisine is a dialogue with the Danish landscape, where every ingredient tells a story of the soil and the season.
              </p>
            </div>

            <div className="space-y-8">
              {features.map((feature, i) => <div key={i} data-index={i} className={`group space-y-3 transition-all duration-500 delay-[${300 + i * 100}ms] ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-background/40 rounded-sm border border-border/30 shadow-sm group-hover:border-primary/40 transition-colors">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-sm font-mono font-medium uppercase tracking-widest text-foreground">
                        {feature.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <div className="relative pt-1">
                    <Progress value={isVisible ? feature.progress : 0} className="h-1 bg-muted/30" />
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="uppercase font-mono text-xs tracking-[0.2em] h-12 px-8 bg-transparent border border-primary/50 hover:bg-primary hover:text-primary-foreground transition-all duration-500 rounded-none">
                <Link to="/TheMenuPage">
                  Explore The Menu <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}